// src/KeywordSuggestionModal.tsx
import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Select,
  Checkbox,
  Tooltip,
  message,
  Spin,
  Tag,
} from 'antd'
import {
  DownOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { isLanguageBeta } from './LanguageConfig'
import { isTutorialModeRequested, isTutorialSandboxModeEnabled } from './utils/tutorialMode'
import {
  metadataFreeRunConsumed,
} from './utils/metadataFreeRuns'

import AIModelSelect from './AIModelSelect';
import { useSelectedAIModel } from './hooks/useSelectedAIModel';
import ModalCostBreakdown, { type ModalCostBreakdownRow } from './components/ModalCostBreakdown';

import { CreditBalance as CreditBalanceType } from './types';

interface KeywordSuggestionModalProps {
  isVisible: boolean
  toggleModal: () => void
  bookId: string
  bookLanguage: string
  genres: string
  tropes: string[]
  keywords: string[]
  blurb: string
  selectedAIModel: string
  fetchCreditBalance: (userId: string) => Promise<void>
  creditBalance: CreditBalanceType | null
  auth: any
  languages: string[]
  betaLanguages: string[]
  fetchBookDetails: () => void
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  targetLanguage: string
  keywordSuggestionFreeRuns?: Record<string, unknown>
  tutorialBypassGenerate?: boolean
  onTutorialGenerateKeywords?: (payload: {
    targetLanguage: string
    keywords: string[]
    selectedOptions: {
      genre: boolean
      tropes: boolean
      keywords: boolean
      blurb: boolean
    }
  }) => void
}

const makeTutorialKeywordSuggestions = (
  language: string,
  existingKeywords: string[],
  sourceLanguage?: string,
): string[] => {
  const normalizedLanguage = String(language || '').trim().toLowerCase()
  const normalizedSourceLanguage = String(sourceLanguage || '').trim().toLowerCase()
  const isOriginalLanguage = Boolean(normalizedLanguage && normalizedLanguage === normalizedSourceLanguage)
  if (normalizedLanguage === 'german') {
    return [
      'Zeitreise-Abenteuer|Time-travel adventure',
      'Science-Fiction-Klassiker|Classic science fiction',
      'Victorianische Zukunftsvision|Victorian future vision',
    ]
  }

  const cleaned = existingKeywords
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .slice(0, 3)

  if (cleaned.length > 0) {
    if (isOriginalLanguage) {
      return cleaned
    }
    return cleaned.map((keyword) => `${keyword} (${language})|${keyword}`)
  }

  if (isOriginalLanguage) {
    return ['Reader keyword 1', 'Genre keyword', 'Theme keyword']
  }

  return [
    `${language} Keyword 1|General keyword`,
    `${language} Keyword 2|Genre keyword`,
    `${language} Keyword 3|Theme keyword`,
  ]
}

const KeywordSuggestionModal: React.FC<KeywordSuggestionModalProps> = ({
  isVisible,
  toggleModal,
  bookId,
  bookLanguage,
  genres,
  tropes,
  keywords,
  blurb,
  selectedAIModel,
  creditBalance,
  auth,
  languages,
  fetchBookDetails,
  isLoading,
  setIsLoading,
  targetLanguage,
  keywordSuggestionFreeRuns,
  tutorialBypassGenerate = false,
  onTutorialGenerateKeywords,
}) => {
  /* Small reusable "BETA" badge */
  const BetaTag = (): JSX.Element => (
    <Tag
      color="orange"
      style={{ marginLeft: 8, fontSize: '0.75em', padding: '0 4px', lineHeight: 1 }}
    >
      BETA
    </Tag>
  )

  const [keywordAIModel, setKeywordAIModel] = useState<string>('')
  const [targetLang, setTargetLang] = useState<string>('')
  const [selectedOptions, setSelectedOptions] = useState({
    genre: false,
    tropes: false,
    keywords: false,
    blurb: false,
  })
  const selectedModelData = useSelectedAIModel(keywordAIModel);
  const [isCostBreakdownModalVisible, setIsCostBreakdownModalVisible] = useState(false)
  const tutorialModeRequested = tutorialBypassGenerate || isTutorialModeRequested() || isTutorialSandboxModeEnabled()
  const targetLanguageOptions = React.useMemo(() => {
    const options: string[] = []
    const seen = new Set<string>()
    ;[bookLanguage, ...languages].forEach((language) => {
      const cleanLanguage = String(language || '').trim()
      const key = cleanLanguage.toLowerCase()
      if (!cleanLanguage || seen.has(key)) return
      seen.add(key)
      options.push(cleanLanguage)
    })
    return options
  }, [bookLanguage, languages])

  /* Sync props to state */
  useEffect(() => {
    setKeywordAIModel(selectedAIModel)
    setTargetLang(targetLanguage || '')
  }, [selectedAIModel, targetLanguage])

  const isAnyOptionSelected = Object.values(selectedOptions).some(Boolean)
  const hasGenre = String(genres || '').trim().length > 0
  const hasTropes = tropes.length > 0
  const hasKeywords = keywords.length > 0
  const hasBlurb = String(blurb || '').trim().length > 0

  const dataSourceDefinitions: Array<{
    key: keyof typeof selectedOptions
    label: string
    testId: string
    available: boolean
    tooltip: string
    detail: string
    value: boolean
  }> = [
    {
      key: 'genre',
      label: 'Genre',
      testId: 'tutorial:keyword-suggestions:data-source-genre',
      available: hasGenre,
      tooltip: 'Genre has not been provided.',
      detail: hasGenre ? genres : 'Add a genre in metadata to use this signal.',
      value: selectedOptions.genre,
    },
    {
      key: 'tropes',
      label: 'Tropes',
      testId: 'tutorial:keyword-suggestions:data-source-tropes',
      available: hasTropes,
      tooltip: 'Tropes have not been provided.',
      detail: hasTropes ? `${tropes.length} trope${tropes.length === 1 ? '' : 's'} available` : 'Add tropes for market-fit suggestions.',
      value: selectedOptions.tropes,
    },
    {
      key: 'keywords',
      label: `${bookLanguage} Keywords`,
      testId: 'tutorial:keyword-suggestions:data-source-keywords',
      available: hasKeywords,
      tooltip: 'Keywords have not been provided.',
      detail: hasKeywords ? `${keywords.length} keyword${keywords.length === 1 ? '' : 's'} available` : 'Add source keywords before reusing them.',
      value: selectedOptions.keywords,
    },
    {
      key: 'blurb',
      label: 'Blurb',
      testId: 'tutorial:keyword-suggestions:data-source-blurb',
      available: hasBlurb,
      tooltip: 'Blurb has not been provided.',
      detail: hasBlurb ? 'Book description available' : 'Add a blurb for story-context suggestions.',
      value: selectedOptions.blurb,
    },
  ]
  const availableSourceCount = dataSourceDefinitions.filter((source) => source.available).length
  const selectedSourceLabels = dataSourceDefinitions
    .filter((source) => source.value)
    .map((source) => source.label)
  const effectiveAvailableCredits = creditBalance ? (creditBalance.available_balance ?? creditBalance.credit_balance ?? null) : null

  const setSourceSelected = (
    key: keyof typeof selectedOptions,
    checked: boolean,
    available: boolean,
  ) => {
    if (!available) return
    setSelectedOptions((current) => ({
      ...current,
      [key]: checked,
    }))
  }

  const toggleSourceSelected = (source: typeof dataSourceDefinitions[number]) => {
    setSourceSelected(source.key, !source.value, source.available)
  }

  const handleSourceKeyDown = (
    event: React.KeyboardEvent<HTMLSpanElement>,
    source: typeof dataSourceDefinitions[number],
  ) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    toggleSourceSelected(source)
  }

  const keywordFreeRunAvailable = Boolean(targetLang) && !metadataFreeRunConsumed(keywordSuggestionFreeRuns, targetLang)

  /* Credit cost is fixed at 500 after the first successful run for a language */
  const calculateKeywordSuggestionCost = (): number =>
    isAnyOptionSelected && !keywordFreeRunAvailable ? 500 : 0

  const formatCredits = React.useCallback((value: number) =>
    new Intl.NumberFormat('en-US').format(Math.max(0, Math.round(value))),
  [])

  const getModelDiscountPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.discount || 0)
    return Math.max(0, value)
  }, [selectedModelData?.discount])

  const getModelPremiumPercent = React.useCallback(() => {
    const value = Number(selectedModelData?.premium || 0)
    return Math.max(0, value)
  }, [selectedModelData?.premium])

  const getNetModelAdjustmentPercent = React.useCallback(() => {
    return getModelPremiumPercent() - getModelDiscountPercent()
  }, [getModelDiscountPercent, getModelPremiumPercent])

  const baseCreditCost = calculateKeywordSuggestionCost()
  const adjustedCreditCost = React.useMemo(() => {
    const netPct = getNetModelAdjustmentPercent()
    if (!netPct) return baseCreditCost
    return Math.max(0, Math.ceil(baseCreditCost * (1 + netPct / 100)))
  }, [baseCreditCost, getNetModelAdjustmentPercent])
  const userHasEnoughCredits = (() => {
    if (tutorialModeRequested) return true
    const avail = effectiveAvailableCredits
    const need = adjustedCreditCost
    if (need === 0) return true
    return typeof avail === 'number' && avail >= need
  })()
  const disableSuggestButton = !isAnyOptionSelected || !userHasEnoughCredits || !targetLang

  const keywordCostRows = React.useMemo<ModalCostBreakdownRow[]>(() => {
    if (!isAnyOptionSelected) return []
    const rows: ModalCostBreakdownRow[] = [
      {
        key: 'keyword-suggestions',
        label: 'Keyword suggestions',
        amountCredits: baseCreditCost,
        explanation: 'Fixed cost for generating localized keyword suggestions.',
        tone: 'info',
      },
    ]
    if (keywordFreeRunAvailable) {
      rows[0] = {
        ...rows[0],
        displayValue: 'Included',
        explanation: 'First keyword suggestion for this language is free.',
        tone: 'success',
      }
    }
    const modelDiscountPct = getModelDiscountPercent()
    const modelPremiumPct = getModelPremiumPercent()
    if (modelDiscountPct > 0) {
      rows.push({
        key: 'model-discount',
        label: 'Model discount',
        displayValue: `${modelDiscountPct}% off`,
        explanation: 'Applied by the selected AI model.',
        tone: 'success',
      })
    }
    if (modelPremiumPct > 0) {
      rows.push({
        key: 'model-premium',
        label: 'Model premium',
        displayValue: `+${modelPremiumPct}%`,
        explanation: 'Applied by the selected AI model.',
        tone: 'warning',
      })
    }
    return rows
  }, [baseCreditCost, getModelDiscountPercent, getModelPremiumPercent, isAnyOptionSelected, keywordFreeRunAvailable])

  /* POST request to backend */
  const handleKeywordSuggestions = async (): Promise<void> => {
    if (disableSuggestButton) return
    setIsLoading(true)

    if (tutorialModeRequested) {
      const tutorialKeywords = makeTutorialKeywordSuggestions(targetLang, keywords, bookLanguage)
      onTutorialGenerateKeywords?.({
        targetLanguage: targetLang,
        keywords: tutorialKeywords,
        selectedOptions,
      })
      toggleModal()
      setIsLoading(false)
      message.success('Keywords generated successfully.')
      return
    }

    if (!auth.currentUser) {
      setIsLoading(false)
      message.error('Please sign in to generate keywords.')
      return
    }

    const payload = {
      userId: auth.currentUser.uid,
      bookId,
      originLanguage: bookLanguage,
      targetLanguage: targetLang,
      options: selectedOptions,
      model: keywordAIModel,
    }

    let success = false

    try {
      toggleModal()
      await axios.post(
        'https://askpoeai-nswdzfky7q-uc.a.run.app/suggest-keywords/',
        payload,
      )
      success = true
      fetchBookDetails()
    } catch {
      success = false
    } finally {
      setIsLoading(false)
      success
        ? message.success('Keywords generated successfully.')
        : message.error('Error generating keywords.')
    }
  }

  /* Convenience renderer for each source option */
  const renderSourceOption = (source: typeof dataSourceDefinitions[number]) => {
    const disabled = !source.available
    return (
      <Tooltip title={disabled ? source.tooltip : ''}>
        <span
          className={`keyword-source-card${source.value ? ' keyword-source-card--selected' : ''}${disabled ? ' keyword-source-card--disabled' : ''}`}
          data-testid={source.testId}
          role="button"
          aria-pressed={source.value}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          onClick={() => toggleSourceSelected(source)}
          onKeyDown={(event) => handleSourceKeyDown(event, source)}
          style={{
            display: 'flex',
            minWidth: 0,
            flexDirection: 'column',
            gap: 4,
            padding: '8px 9px',
            borderRadius: 8,
            border: source.value
              ? '1px solid var(--accent-primary)'
              : '1px solid color-mix(in srgb, var(--border-subtle) 76%, transparent)',
            background: source.value
              ? 'color-mix(in srgb, var(--surface-info) 58%, var(--surface-base) 42%)'
              : disabled
                ? 'color-mix(in srgb, var(--surface-muted) 74%, transparent)'
                : 'var(--surface-muted)',
            color: disabled ? 'var(--text-secondary)' : 'var(--text-main)',
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <span style={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 6 }}>
            <Checkbox
              checked={source.value}
              onClick={(event) => event.stopPropagation()}
              onChange={(e) => setSourceSelected(source.key, e.target.checked, source.available)}
              disabled={disabled}
              aria-label={source.label}
            />
            <span style={{ minWidth: 0, fontWeight: 700 }}>{source.label}</span>
            {source.value && (
              <Tag style={{ marginInlineEnd: 0, borderColor: 'var(--accent-primary)' }}>
                Selected
              </Tag>
            )}
          </span>
          <span style={{ color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.3 }}>
            {source.detail}
          </span>
        </span>
      </Tooltip>
    )
  }

  const footerBlockerMessages = [
    !targetLang ? 'choose target language' : '',
    !isAnyOptionSelected ? 'select data sources' : '',
    !userHasEnoughCredits && !tutorialModeRequested ? 'top-up needed' : '',
  ].filter(Boolean)
  const footerBlockerMessage = footerBlockerMessages.join('; ')
  const modelAdjustmentNode = (() => {
    if (!isAnyOptionSelected) return null
    const modelDiscountPct = getModelDiscountPercent()
    const modelPremiumPct = getModelPremiumPercent()
    if (!modelDiscountPct && !modelPremiumPct) return null
    return (
      <span className="keyword-suggestion-modal-footer-tags">
        {modelDiscountPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--success">
            {modelDiscountPct}% OFF
          </Tag>
        )}
        {modelPremiumPct > 0 && (
          <Tag className="modal-token-tag modal-token-tag--warning">
            +{modelPremiumPct}% Premium
          </Tag>
        )}
      </span>
    )
  })()
  const footerSummary = (
    <div className="translation-modal-footer-summary" aria-live="polite" data-testid="tutorial:keyword-suggestions:cost-summary">
      {isAnyOptionSelected ? (
        <span>
          <strong>{formatCredits(adjustedCreditCost)} credits</strong> required
          {keywordFreeRunAvailable ? ' · first suggestion free' : ''}
        </span>
      ) : (
        <span>Select data sources to calculate cost</span>
      )}
      <span className="translation-modal-footer-summary__separator">·</span>
      <span>
        <strong>
          {typeof effectiveAvailableCredits === 'number'
            ? formatCredits(effectiveAvailableCredits)
            : tutorialModeRequested
              ? 'Tutorial'
              : 'Loading...'}
        </strong>{' '}
        available
      </span>
      {modelAdjustmentNode}
      {footerBlockerMessage ? (
        <>
          <span className="translation-modal-footer-summary__separator">·</span>
          <span className="keyword-suggestion-modal-footer-blocker">Action needed: {footerBlockerMessage}</span>
        </>
      ) : null}
      <Button
        type="link"
        size="small"
        className="translation-modal-footer-summary__details"
        aria-label="View cost breakdown"
        onClick={() => setIsCostBreakdownModalVisible(true)}
      >
        View details
      </Button>
    </div>
  )

  return (
    <>
    <Modal
      title={
        <div style={{ width: '100%', textAlign: 'center' }}>
          Keyword Suggestions
        </div>
      }
      visible={isVisible}
      onCancel={toggleModal}
      centered
      className="translation-modal-footer-stable"
      width="min(820px, calc(100vw - 32px))"
      bodyStyle={{
        maxHeight: 'min(76vh, calc(100vh - 220px))',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px 16px 12px',
      }}
      footer={[
        <React.Fragment key="keyword-summary">
          {footerSummary}
        </React.Fragment>,
        <Button key="cancel" onClick={toggleModal}>
          Cancel
        </Button>,
        <Button
          key="suggest"
          type="primary"
          onClick={handleKeywordSuggestions}
          disabled={disableSuggestButton}
          data-testid="tutorial:keyword-suggestions:generate-button"
        >
          Generate Keywords
        </Button>,
      ]}
    >
      <Spin spinning={isLoading}>
        <div className="translation-modal-body" data-testid="tutorial:keyword-suggestions:modal">
          <section
            className="translation-modal-block translation-modal-block--scope"
            aria-labelledby="keyword-suggestion-scope-heading"
            data-testid="tutorial:keyword-suggestions:scope"
          >
            <div className="translation-modal-block-header">
              <div>
                <h3 id="keyword-suggestion-scope-heading">Suggestion scope</h3>
                <p>
                  Generate reader-facing search terms from <strong>{bookLanguage || 'the source language'}</strong>.
                </p>
              </div>
            </div>
            <div className="translation-scope-route-row">
              <div className="translation-scope-field translation-scope-field--readonly">
                <span className="translation-modal-kicker">Origin language</span>
                <strong>{bookLanguage || 'Not selected'}</strong>
              </div>
              <div className="translation-scope-route-divider" aria-hidden="true">
                to
              </div>
              <div className="translation-scope-field">
                <span className="translation-modal-kicker">Target language</span>
                <div data-testid="tutorial:keyword-suggestions:target-language">
                  <Select
                    value={targetLang}
                    onChange={setTargetLang}
                    style={{ width: '100%' }}
                    placeholder="Select a target language"
                    suffixIcon={
                      <span className="ant-select-suffix">
                        <DownOutlined className="modal-token-icon" />
                      </span>
                    }
                  >
                    {targetLanguageOptions
                      .map((lang) => {
                        const optionSlug = String(lang || '')
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '')
                        const isOriginalLanguage = lang.toLowerCase() === String(bookLanguage || '').trim().toLowerCase()
                        return (
                          <Select.Option key={lang} value={lang}>
                            {optionSlug === 'german' ? (
                              <span data-testid="tutorial:keyword-suggestions:target-language-option:german">
                                {isOriginalLanguage ? `Original (${lang})` : lang}
                                {isLanguageBeta(lang) && <BetaTag />}
                              </span>
                            ) : (
                              <span data-testid={`tutorial:keyword-suggestions:target-language-option:${optionSlug}`}>
                                {isOriginalLanguage ? `Original (${lang})` : lang}
                                {isLanguageBeta(lang) && <BetaTag />}
                              </span>
                            )}
                          </Select.Option>
                        )
                      })}
                  </Select>
                </div>
              </div>
            </div>
          </section>

          <section
            className="translation-modal-block"
            aria-labelledby="keyword-suggestion-settings-heading"
            data-testid="tutorial:keyword-suggestions:settings"
          >
            <div className="translation-modal-block-header">
              <div>
                <h3 id="keyword-suggestion-settings-heading">Generation settings</h3>
                <p>Choose the model used to create localized store keywords.</p>
              </div>
            </div>
            <div className="translation-quality-grid translation-quality-grid--compact keyword-suggestion-model-grid">
              <div className="translation-quality-control">
                <span className="translation-modal-kicker">AI model</span>
                <div data-testid="tutorial:keyword-suggestions:ai-model">
                  <AIModelSelect
                    workflow="keywords"
                    value={keywordAIModel}
                    onChange={setKeywordAIModel}
                    language={targetLang}
                    sourceLanguage={bookLanguage}
                    isLangTesterUser={false}
                  />
                </div>
              </div>
            </div>
          </section>

          <section
            className="translation-modal-block"
            aria-labelledby="keyword-suggestion-sources-heading"
            data-testid="tutorial:keyword-suggestions:data-sources"
          >
            <div className="translation-modal-block-header">
              <div>
                <h3 id="keyword-suggestion-sources-heading">Data sources</h3>
                <p>Select the metadata ScribeShadow should use. Stronger source coverage usually produces more useful store keywords.</p>
              </div>
              <Tag style={{ borderColor: 'var(--border-subtle)', color: 'var(--text-secondary)' }}>
                {availableSourceCount}/4 available
              </Tag>
            </div>
            <div className="translation-quality-grid">
              {dataSourceDefinitions.map((source) => (
                <React.Fragment key={source.key}>
                  {renderSourceOption(source)}
                </React.Fragment>
              ))}
            </div>
            {selectedSourceLabels.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 9 }} aria-live="polite">
                {selectedSourceLabels.map((label) => (
                  <Tag key={label} style={{ marginInlineEnd: 0, borderColor: 'var(--accent-primary)' }}>
                    <CheckCircleOutlined style={{ marginRight: 4, color: 'var(--accent-primary)' }} />
                    {label}
                  </Tag>
                ))}
              </div>
            )}
          </section>

        </div>
      </Spin>
    </Modal>
    <Modal
      title="Cost breakdown"
      visible={isCostBreakdownModalVisible}
      onCancel={() => setIsCostBreakdownModalVisible(false)}
      width={560}
      footer={[
        <Button key="close-keyword-cost-breakdown" onClick={() => setIsCostBreakdownModalVisible(false)}>
          Close
        </Button>,
      ]}
    >
      <div data-testid="keyword-cost-breakdown-modal">
        <ModalCostBreakdown
          rows={keywordCostRows}
          totalCredits={isAnyOptionSelected ? adjustedCreditCost : null}
          availableCredits={effectiveAvailableCredits}
          emptyMessage="Select data sources to calculate cost."
          formatCredits={formatCredits}
          footerNote={
            isAnyOptionSelected && adjustedCreditCost !== baseCreditCost
              ? `Standard estimate before adjustments: ${formatCredits(baseCreditCost)} credits.`
              : undefined
          }
        />
      </div>
    </Modal>
    </>
  )
}

export default KeywordSuggestionModal
